import { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate, useParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Languages, MapPin, Star } from "lucide-react";
import axiosInstance from "../api/Axiosinstance";
import { ENDPOINTS } from "../api/Endpoints";
import assistantsService, {
  normalizeAssistantListItem,
} from "../api/services/assistantsService";
import { useAppConfigContext } from "../context/AppConfigContext";
import { normalizeNigeriaState } from "../constants/nigeriaStates";

/**
 * Editorial copy for the destinations Pulo features on the home/stays/assistant
 * carousels. Only the hero imagery and blurb live here — every listing the page
 * renders (assistants, accommodations) is fetched live from the API and filtered
 * to the destination.
 *
 * `state` must match a canonical Nigerian state name because that is what
 * provider profiles are stored with (note Abuja is persisted as "Federal
 * Capital Territory"); `city` is matched case-insensitively against
 * location.city on stays.
 */
const DESTINATIONS = {
  abuja: {
    name: "Abuja, Nigeria",
    city: "Abuja",
    state: "Federal Capital Territory",
    hero: "/images/abuja.jpg",
    description:
      "Abuja is not just about politics and governance—it's also a growing hub for business, international diplomacy, and tourism. The city hosts embassies, international organizations, and vibrant commercial districts.",
    highlights: [
      { title: "Wonderland", image: "/images/wonderland.jpg" },
      { title: "The Vue", image: "/images/vue.jpg" },
      { title: "The Capital Hub", image: "/images/capitalhub.jpg" },
    ],
  },
  lagos: {
    name: "Lagos, Nigeria",
    city: "Lagos",
    state: "Lagos",
    hero: "/images/lagos.jpg",
    description:
      "Nigeria's commercial heart. Lagos pairs Atlantic beaches and island nightlife with a restless creative scene, from Lekki and Victoria Island to the markets and music of the mainland.",
  },
  calabar: {
    name: "Calabar, Nigeria",
    city: "Calabar",
    state: "Cross River",
    hero: "/images/calabar.jpg",
    description:
      "Calabar is Nigeria's culture and nature escape—rainforest reserves, riverside seafood, and a carnival the whole country travels for.",
  },
  maiduguri: {
    name: "Maiduguri, Nigeria",
    city: "Maiduguri",
    state: "Borno",
    hero: "/images/maiduguri.jpg",
    description:
      "Maiduguri offers landmarks, great meals and a rich northern culture, with warm hospitality and markets worth taking your time in.",
  },
};

const titleCase = (value = "") =>
  value
    .split(/[\s-_]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

/** Unwraps the various list shapes the API layer can hand back. */
const extractResults = (res) =>
  res?.data?.items ||
  res?.data?.results ||
  res?.items ||
  res?.results ||
  (Array.isArray(res?.data) ? res.data : null) ||
  (Array.isArray(res) ? res : []);

const formatCount = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "0";
  return parsed.toLocaleString();
};

const CAROUSEL_CLASSES =
  "flex gap-4 overflow-x-auto snap-x snap-mandatory sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:gap-4 sm:overflow-visible [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden";

function ExploreCard({ title, image }) {
  return (
    <div className="relative rounded-2xl overflow-hidden shadow-sm border border-slate-100 hover:-translate-y-1 transition-transform bg-white h-56 sm:h-64 flex-shrink-0 w-[85%] sm:w-auto snap-center">
      <img src={image} alt={title} className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/15 to-transparent" aria-hidden="true" />
      <p className="absolute bottom-4 left-4 text-2xl font-bold text-white drop-shadow-md">
        {title}
      </p>
    </div>
  );
}

function CardSkeleton() {
  return (
    <div className="rounded-xl border border-gray-200 p-[6px] animate-pulse flex-shrink-0 w-[85%] sm:w-auto snap-center">
      <div className="w-full h-60 rounded-lg bg-gray-200" />
      <div className="pt-3 space-y-2">
        <div className="h-4 w-2/3 rounded bg-gray-200" />
        <div className="h-3 w-1/2 rounded bg-gray-200" />
        <div className="h-3 w-1/3 rounded bg-gray-200" />
      </div>
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <p className="rounded-xl border border-dashed border-gray-300 px-6 py-10 text-center text-sm text-gray-500">
      {message}
    </p>
  );
}

function AssistantCard({ assistant, onView }) {
  return (
    <article
      onClick={onView}
      className="bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm p-[6px] flex flex-col flex-shrink-0 w-[85%] sm:w-auto snap-center cursor-pointer"
    >
      <img
        src={assistant.image || "/images/travel-img.png"}
        alt={assistant.name}
        className="w-full h-60 object-cover object-top rounded-xl"
      />

      <div className="pt-4 space-y-3">
        <div className="space-y-1">
          <p className="text-base font-semibold text-black">
            {assistant.name || "Pulo Assistant"}
          </p>
          <div className="text-sm font-medium text-black">
            {formatCount(assistant.completedBookings)} completed bookings
          </div>
        </div>

        <div className="flex items-center gap-1 text-xs font-medium text-black">
          <MapPin className="w-4 h-4 text-black" />
          <span>{assistant.location || "Location unavailable"}</span>
        </div>

        <div className="flex justify-between items-center">
          <div className="flex items-center gap-1 text-xs font-medium text-black">
            <Languages className="w-4 h-4 text-black" />
            <span>{assistant.languages || "Languages not listed"}</span>
          </div>

          <button
            className="text-xs font-medium text-[#570033] underline underline-offset-4 pt-2"
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onView();
            }}
          >
            View
          </button>
        </div>
      </div>
    </article>
  );
}

function StayCard({ stay, onView }) {
  return (
    <article
      onClick={onView}
      className="bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm p-[6px] flex flex-col flex-shrink-0 w-[85%] sm:w-auto snap-center cursor-pointer"
    >
      <img
        src={stay.image}
        alt={stay.title}
        className="w-full h-60 object-cover rounded-lg"
      />

      <div className="pt-3 space-y-2">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1 w-full">
            <p className="text-md font-semibold text-black">{stay.title}</p>
            {stay.location && (
              <div className="flex items-center gap-1 text-[10px] font-medium text-gray-600">
                <MapPin className="w-4 h-4 text-gray-600" />
                <span>{stay.location}</span>
              </div>
            )}
            {stay.details && (
              <div className="flex items-center gap-1 font-medium">
                <img src="/icons/bed-double.svg" alt="" className="w-4 h-4" />
                <p className="text-xs text-gray-600">{stay.details}</p>
              </div>
            )}
          </div>

          {stay.rating > 0 && (
            <div className="flex items-center gap-1 text-sm font-medium text-black">
              <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
              <span>{stay.rating}</span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between w-full">
          <p className="text-xs font-medium text-gray-800">{stay.price}</p>
          <button
            className="text-xs font-medium text-[#570033] underline underline-offset-4"
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onView();
            }}
          >
            View
          </button>
        </div>
      </div>
    </article>
  );
}

function Explore() {
  const { city } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { isServiceEnabled, isLoading: isConfigLoading } =
    useAppConfigContext();

  const staysEnabled = isServiceEnabled("stays");
  const assistantsEnabled = isServiceEnabled("puloAssistants");
  const ridesEnabled = isServiceEnabled("rides");

  const destination = useMemo(() => {
    const slug = city?.toLowerCase();
    if (slug && DESTINATIONS[slug]) return DESTINATIONS[slug];

    // A destination we have no editorial entry for: fall back to whatever the
    // carousel handed us, and still filter listings by the slug itself.
    const label = titleCase(state?.title || slug || "");
    return {
      name: state?.title || (label ? `${label}, Nigeria` : "Nigeria"),
      city: label,
      state: normalizeNigeriaState(label),
      hero: state?.image || state?.src || "/images/home-hero.jpg",
      description:
        state?.subtitle ||
        "Discover local favorites, top activities, and trusted Pulo assistants.",
    };
  }, [city, state]);

  const shortName = destination.name.split(",")[0];

  const [assistants, setAssistants] = useState([]);
  const [assistantsLoading, setAssistantsLoading] = useState(false);
  const [stays, setStays] = useState([]);
  const [staysLoading, setStaysLoading] = useState(false);

  useEffect(() => {
    if (!assistantsEnabled) {
      setAssistants([]);
      return undefined;
    }

    let active = true;
    setAssistantsLoading(true);

    (async () => {
      try {
        const res = await assistantsService.getAssistants({
          ...(destination.state ? { state: destination.state } : {}),
          limit: 3,
        });
        const results = extractResults(res);
        if (!active) return;
        setAssistants(
          Array.isArray(results) ? results.map(normalizeAssistantListItem) : []
        );
      } catch (error) {
        if (!active) return;
        setAssistants([]);
        console.error("Error fetching assistants for explore:", error);
      } finally {
        if (active) setAssistantsLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [assistantsEnabled, destination.state]);

  useEffect(() => {
    if (!staysEnabled) {
      setStays([]);
      return undefined;
    }

    let active = true;
    setStaysLoading(true);

    (async () => {
      try {
        const res = await axiosInstance.get(ENDPOINTS.TOURS.STAYS, {
          params: {
            ...(destination.city ? { city: destination.city } : {}),
            isActive: true,
            limit: 6,
          },
        });
        const results = res?.data?.data?.results || [];
        if (!active) return;
        setStays(
          results.map((stay) => ({
            id: stay.id || stay._id,
            title: stay.title,
            location: stay.locationSummary,
            details: stay.propertySummary,
            price: stay.formattedPrice,
            rating: Number(stay.rating) || 0,
            image: stay.images?.[0]?.url || "/images/stay-hero.jpg",
          }))
        );
      } catch (error) {
        if (!active) return;
        setStays([]);
        console.error("Error fetching stays for explore:", error);
      } finally {
        if (active) setStaysLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [staysEnabled, destination.city]);

  const viewAssistant = useCallback(
    (assistant) =>
      navigate(`/assistant/${assistant.id}`, { state: { assistant } }),
    [navigate]
  );

  const viewStay = useCallback(
    (stay) => navigate(`/stays/${stay.id}`, { state: stay }),
    [navigate]
  );

  return (
    <div className="bg-white min-h-screen">
      <Header />

      <main className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-0 lg:max-w-[80%] space-y-12 md:space-y-16 pb-16">
        <section className="rounded-3xl overflow-hidden shadow-md ">
          <img
            src={destination.hero}
            alt={destination.name}
            className="w-full h-[260px] sm:h-[360px] md:h-[420px] object-cover"
          />
        </section>

        <section className="space-y-3">
          <h1 className="text-3xl sm:text-4xl font-bold text-[#570033]">
            {destination.name}
          </h1>
          <p className="text-sm sm:text-base text-slate-700 leading-relaxed max-w-3xl">
            {destination.description}
          </p>
        </section>

        {destination.highlights?.length > 0 && (
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-[#570033]">
              Explore {shortName}
            </h2>
            <div className={CAROUSEL_CLASSES}>
              {destination.highlights.map((item) => (
                <ExploreCard key={item.title} {...item} />
              ))}
            </div>
          </section>
        )}

        {assistantsEnabled && (
          <section className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-[#570033]">
                Pulo Assistants in {shortName}
              </h2>
              <NavLink
                to="/travel-assistant"
                className="text-sm font-semibold text-[#570033] underline"
              >
                See more
              </NavLink>
            </div>

            {isConfigLoading || assistantsLoading ? (
              <div className={CAROUSEL_CLASSES}>
                {[0, 1, 2].map((key) => (
                  <CardSkeleton key={key} />
                ))}
              </div>
            ) : assistants.length > 0 ? (
              <div className={CAROUSEL_CLASSES}>
                {assistants.map((assistant) => (
                  <AssistantCard
                    key={assistant.id}
                    assistant={assistant}
                    onView={() => viewAssistant(assistant)}
                  />
                ))}
              </div>
            ) : (
              <EmptyState
                message={`No Pulo assistants are listed in ${shortName} yet. Browse assistants across Nigeria instead.`}
              />
            )}
          </section>
        )}

        {staysEnabled && (
          <section className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-[#570033]">
                Accommodations in {shortName}
              </h2>
              <NavLink
                to="/stays"
                className="text-sm font-semibold text-[#570033] underline"
              >
                See more
              </NavLink>
            </div>

            {isConfigLoading || staysLoading ? (
              <div className={CAROUSEL_CLASSES}>
                {[0, 1, 2, 3, 4, 5].map((key) => (
                  <CardSkeleton key={key} />
                ))}
              </div>
            ) : stays.length > 0 ? (
              <div className={CAROUSEL_CLASSES}>
                {stays.map((stay) => (
                  <StayCard
                    key={stay.id}
                    stay={stay}
                    onView={() => viewStay(stay)}
                  />
                ))}
              </div>
            ) : (
              <EmptyState
                message={`No stays are listed in ${shortName} yet. Browse every Pulo stay instead.`}
              />
            )}
          </section>
        )}

        {ridesEnabled && (
          <section className="rounded-2xl bg-[#570033] px-6 py-8 sm:px-10 sm:py-10 text-white flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="space-y-1">
              <h2 className="text-xl font-semibold">
                Getting around {shortName}
              </h2>
              <p className="text-sm text-white/80 max-w-xl">
                Book a vetted Pulo driver for airport pickups, day trips and
                everything in between.
              </p>
            </div>
            <NavLink
              to="/rides"
              className="inline-flex justify-center rounded-lg bg-white px-6 py-3 text-sm font-semibold text-[#570033] hover:bg-white/90 transition-colors"
            >
              Book a ride
            </NavLink>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
}

export default Explore;
